import {
  Add,
  CopyAll,
  DangerousOutlined,
  DeleteOutline,
  SearchOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dropdown,
  Input,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmDeleteModal from "../../components/Inventory/ConfirmDeleteModal";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import { theme } from "../../components/Theme";
import { InventoryService } from "../../services/inventory.service";
import { getActiveBusiness } from "../../store/session";
import { getInitials } from "../../utils/misc";
import { IAccountBusiness } from "./components/InviteModal";
import useContainer from "./useContainer";
import debounce from "lodash.debounce";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import {
  EllipsisVerticalIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import ConfirmDuplicateModal from "../../components/Inventory/ConfirmDuplicateModal";
import { CatalogType } from "../../types/catalog";
import { getCountryInfo } from "../../utils/country";
import { useQuery } from "@tanstack/react-query";
import CatalogueMetadata from "../../components/Catalogue/CatalogueMetadata";
import { If } from "../../components/Condition";

const statusFilter = ["Active", "Expired"];
const typeFilter = ["Public", "Private"];

const ListCatalogue = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const catalogStatus = searchParams.get("status");
  const catalogType = searchParams.get("type");
  const query = searchParams.get("q");

  const navigate = useNavigate();

  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [activeCatalog, setActiveCatalog] = useState<CatalogType | null>(null);
  const [selectedCatalog, setSelectedCatalog] = useState<string>("");

  const {
    fetchAllCatalogs,
    removeCatalog,
    pagination,
    isLoading,
    setIsLoading,
    hasMore,
    catalogs,
    confirmDelete,
    setConfirmDelete,
    setShowAlert,
    setAlertMessage,
  } = useContainer();

  // const initiateCatalogs = () => {
  //   fetchAllCatalogs({
  //     businessId: getActiveBusiness()?.id ?? "",
  //     limit: 10,
  //     offset: 0,
  //     catalogType: catalogType?.toUpperCase() as "PUBLIC" | "PRIVATE",
  //     searchTerm: query,
  //     isReset: true,
  //     publicCatalogPeriodType: catalogStatus?.toUpperCase() ?? "ALL",
  //   });
  // };

  useEffect(() => {
    fetchAllCatalogs({
      businessId: getActiveBusiness()?.id ?? "",
      limit: 10,
      offset: 0,
      catalogType: catalogType?.toUpperCase() as "PUBLIC" | "PRIVATE",
      searchTerm: query,
      isReset: true,
      publicCatalogPeriodType: catalogStatus?.toUpperCase() ?? "ALL",
    });
  }, [query, catalogStatus, catalogType]);

  const handleRemove = () => {
    removeCatalog(
      selectedCatalog,
      catalogType as "PUBLIC" | "PRIVATE",
      catalogStatus
    );
  };

  const handleDuplicate = () => {
    setIsLoading(true);

    if (!activeCatalog) return;

    const businessId = getActiveBusiness()?.id;

    if (activeCatalog.id && businessId) {
      InventoryService.duplicateCatalog({
        catalogId: activeCatalog.id,
      })
        .then((res) => {
          if (res.id) {
            setShowAlert(true);
            setAlertMessage(
              t("catalog.inventory.duplicateCatalog.successMessage")
            );

            setTimeout(() => {
              setShowAlert(false);
            }, 3000);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setIsLoading(false);
          window.location.href = `/client/catalogues/list?type=${
            activeCatalog?.catalogType.toLowerCase() ?? "public"
          }`;
        });
    }
  };

  const debouncedSetSearchTerm = useCallback(
    debounce((value: string) => {
      if (value === "") {
        searchParams.delete("q");
      } else {
        searchParams.set("q", value);
      }
      setSearchParams(searchParams);
    }, 300),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearchTerm(event.target.value);
  };

  const CatalogueView = () => {
    return (
      <List sx={{ marginTop: 3 }}>
        <InfiniteScroll
          pageStart={0}
          loadMore={(_page: number) => {
            fetchAllCatalogs({
              businessId: getActiveBusiness()?.id ?? "",
              limit: 10,
              offset: pagination.limit * pagination.current,
              catalogType: catalogType?.toUpperCase() as "PUBLIC" | "PRIVATE",
              searchTerm: query,
              isReset: false,
              publicCatalogPeriodType: catalogStatus?.toUpperCase() ?? "ALL",
            });
          }}
          useWindow={false}
          hasMore={hasMore}
          loader={
            <div className="loader" key={0}>
              Loading ...
            </div>
          }
        >
          {catalogs.map((cat) => {
            return (
              <CatalogueCard
                key={cat.id}
                catalog={cat}
                handleDelete={() => {
                  setSelectedCatalog(cat.id);
                  setConfirmDelete(true);
                }}
                handleDuplicate={() => {
                  setConfirmDuplicate(true);
                  setActiveCatalog(cat);
                }}
              />
            );
          })}
        </InfiniteScroll>
      </List>
    );
  };

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        pb: "100px",
        width: "100%",
        position: "relative",
      }}
    >
      {!isLoading && (
        <>
          {/* search toolbar */}
          <Stack
            direction={{ sx: "column", md: "row" }}
            sx={{
              justifyContent: "space-between",
              alignItems: { md: "center" },
              gap: { xs: "24px", md: 0 },
              mt: "24px",
            }}
          >
            <Stack
              direction={"row"}
              gap={"16px"}
              width={{ xs: "100%", md: "50%" }}
            >
              <Input
                defaultValue={query ?? ""}
                variant="linkz-input-search"
                size="sm"
                name="searchFormData.search"
                placeholder={t("catalog.searchCatalogPlaceholder")}
                startDecorator={<SearchOutlined />}
                sx={{
                  flexGrow: 1,
                  width: { xs: "100%", md: "400px" },
                  height: { xs: "44px", md: "40px" },
                }}
                onChange={handleChange}
              />

              {/* catalog status filter lg */}
              <Select
                value={catalogStatus ?? ""}
                onChange={(
                  _event: React.SyntheticEvent | null,
                  newValue: string | null
                ) => {
                  if (newValue || newValue === "") {
                    const newParams = new URLSearchParams(searchParams);

                    if (newValue === "") {
                      newParams.delete("status");
                    } else {
                      newParams.set("status", encodeURIComponent(newValue));
                    }
                    setSearchParams(newParams);
                  }
                }}
                slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
                size="sm"
                defaultValue={catalogStatus ?? ""}
                sx={{
                  display: { xs: "none", md: "flex" },
                  paddingY: "10px",
                  paddingX: "16px",
                  fontWeight: 600,
                  gap: "8px",
                  color: theme.colorSchemes.light.palette.others.neutral_dark,
                  borderColor:
                    theme.colorSchemes.light.palette.others.neutral_dark,
                  width: "20%",
                }}
                indicator={<ChevronDownIcon width={18} height={18} />}
              >
                <Option value={""}>{t("catalog.filterStatusAll")}</Option>
                {statusFilter.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>

              {/* catalog type filter lg */}
              <Select
                value={catalogType ?? ""}
                onChange={(
                  _event: React.SyntheticEvent | null,
                  newValue: string | null
                ) => {
                  if (newValue || newValue === "") {
                    const newParams = new URLSearchParams(searchParams);

                    if (newValue === "") {
                      newParams.delete("type");
                    } else {
                      newParams.set("type", encodeURIComponent(newValue));
                    }
                    setSearchParams(newParams);
                  }
                }}
                slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
                size="sm"
                defaultValue={catalogType ?? ""}
                sx={{
                  display: { xs: "none", md: "flex" },
                  paddingY: "10px",
                  paddingX: "16px",
                  fontWeight: 600,
                  gap: "8px",
                  color: theme.colorSchemes.light.palette.others.neutral_dark,
                  borderColor:
                    theme.colorSchemes.light.palette.others.neutral_dark,
                  width: "25%",
                }}
                indicator={<ChevronDownIcon width={18} height={18} />}
              >
                <Option value={""}>{t("catalog.filterTypeAll")}</Option>
                {typeFilter.map((s) => (
                  <Option key={s} value={s.toUpperCase()}>
                    {s}
                  </Option>
                ))}
              </Select>
            </Stack>

            {/* create catalog button */}
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              gap={"16px"}
            >
              {/* catalog status filter sm */}
              <Select
                value={catalogStatus ?? ""}
                onChange={(
                  _event: React.SyntheticEvent | null,
                  newValue: string | null
                ) => {
                  if (newValue || newValue === "") {
                    const newParams = new URLSearchParams(searchParams);

                    if (newValue === "") {
                      newParams.delete("status");
                    } else {
                      newParams.set("status", encodeURIComponent(newValue));
                    }
                    setSearchParams(newParams);
                    // setSelectedFilter(newValue);
                  }
                }}
                slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
                size="sm"
                defaultValue={catalogStatus ?? ""}
                sx={{
                  paddingY: { sx: "8px", md: "10px" },
                  paddingX: { sx: "12px", md: "16px" },
                  fontWeight: 600,
                  gap: "8px",
                  color: theme.colorSchemes.light.palette.others.neutral_dark,
                  borderColor:
                    theme.colorSchemes.light.palette.others.neutral_dark,
                  width: "100%",
                  display: { xs: "flex", md: "none" },
                }}
                indicator={<ChevronDownIcon width={18} height={18} />}
              >
                <Option value={""}>{t("catalog.filterStatusAll")}</Option>
                {statusFilter.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>

              {/* catalog type filter sm */}
              <Select
                value={catalogType ?? ""}
                onChange={(
                  _event: React.SyntheticEvent | null,
                  newValue: string | null
                ) => {
                  if (newValue || newValue === "") {
                    const newParams = new URLSearchParams(searchParams);

                    if (newValue === "") {
                      newParams.delete("type");
                    } else {
                      newParams.set("type", encodeURIComponent(newValue));
                    }
                    setSearchParams(newParams);
                    // setSelectedFilter(newValue);
                  }
                }}
                slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
                size="sm"
                defaultValue={catalogType ?? ""}
                sx={{
                  paddingY: { sx: "8px", md: "10px" },
                  paddingX: { sx: "12px", md: "16px" },
                  fontWeight: 600,
                  gap: "8px",
                  color: theme.colorSchemes.light.palette.others.neutral_dark,
                  borderColor:
                    theme.colorSchemes.light.palette.others.neutral_dark,
                  width: "100%",
                  display: { xs: "flex", md: "none" },
                }}
                indicator={<ChevronDownIcon width={18} height={18} />}
              >
                <Option value={""}>{t("catalog.filterTypeAll")}</Option>
                {typeFilter.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>

              <Button
                // size="sm"
                startDecorator={<Add />}
                onClick={() => {
                  const createCatalogPayload = {
                    business: {
                      id: getActiveBusiness()?.id ?? "",
                    },
                    catalogType: "PUBLIC",
                    items: [],
                    step: 1,
                    currency: getCountryInfo(
                      getActiveBusiness()?.countryCode ?? ""
                    )?.currency,
                  };

                  localStorage.setItem(
                    "new-catalog",
                    JSON.stringify(createCatalogPayload)
                  );
                  navigate("/client/catalogues/create?type=" + "PUBLIC");
                }}
                sx={{
                  borderRadius: "6px",
                  fontSize: { xs: "12px", md: "14px" },
                  display: { xs: "none", md: "flex" },
                }}
              >
                {t("catalog.createCatalogBtn")}
              </Button>
            </Stack>
          </Stack>

          {catalogs.length > 0 ? (
            <CatalogueView />
          ) : (
            <Stack justifyContent={"center"} alignItems={"center"} mt={"32px"}>
              <DangerousOutlined
                color="primary"
                sx={{
                  width: "150px !important",
                  height: "150px !important",
                }}
              />
              <br />
              <Typography fontWeight={"lg"} fontSize={"sm"}>
                {t("catalog.emptyCatalogPrompt")}
              </Typography>
            </Stack>
          )}

          <Stack
            direction={"row"}
            sx={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              display: { xs: "flex", md: "none" },
              p: "16px",
              position: "fixed",
              bottom: 0,
              left: 0,
              bgcolor: theme.palette.others.cart_toolbar_bg,
              zIndex: 30
            }}
          >
            <Button
              // size="sm"
              fullWidth
              startDecorator={<Add />}
              onClick={() => {
                const createCatalogPayload = {
                  business: {
                    id: getActiveBusiness()?.id ?? "",
                  },
                  catalogType: "PUBLIC",
                  items: [],
                  step: 1,
                  currency: getCountryInfo(
                    getActiveBusiness()?.countryCode ?? ""
                  )?.currency,
                };

                localStorage.setItem(
                  "new-catalog",
                  JSON.stringify(createCatalogPayload)
                );
                navigate("/client/catalogues/create?type=" + "PUBLIC");
              }}
              sx={{
                borderRadius: "6px",
                fontSize: { xs: "12px", md: "14px" },
                display: { xs: "flex", md: "none" },
              }}
            >
              {t("catalog.createCatalogBtn")}
            </Button>
          </Stack>
        </>
      )}

      {/* modal */}
      <ConfirmDeleteModal
        show={confirmDelete}
        onClick={handleRemove}
        onClose={() => {
          setConfirmDelete(false);
        }}
      />
      <ConfirmDuplicateModal
        show={confirmDuplicate}
        catalogType={
          t(
            `catalog.catalogType.${activeCatalog?.catalogType.toLowerCase()}`
          ) ?? "private"
        }
        onClick={handleDuplicate}
        onClose={() => {
          setConfirmDuplicate(false);
        }}
      />
      <LoadingModal isLoading={isLoading} />
    </Sheet>
  );
};

const CatalogInvitationData = ({ catalogId }: { catalogId: string }) => {
  const { isPending, data } = useQuery({
    queryKey: ["catalog-invitations", catalogId],
    queryFn: () => InventoryService.getCatalogInvitationsData({ catalogId }),
  });
  return isPending ? (
    <CircularProgress />
  ) : (
    <UserListButton
      accounts={
        data?.accounts?.map((account: any) => ({
          ...account,
          newlySelectedBusinessId: account?.recipientBusiness,
          identifier:
            account?.authStrategy === "PHONE_AUTH"
              ? account.phoneNumber
              : account?.email,
        })) ?? []
      }
    />
  );
};

const UserListButton = ({ accounts }: { accounts: IAccountBusiness[] }) => {
  const accountCount = accounts.length;

  const renderAccount = (account: any, index: number) => {
    return (
      <Box
        key={index}
        sx={{
          display: "flex",
          border: "1px solid",
          borderColor: theme.palette.others.disabled,
          fontWeight: "bold",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          fontSize: "sm",
          color: theme.palette.primary[500],
          bgcolor: theme.palette.primary[50],
          borderRadius: "50%",
          ml: index > 0 ? -1.5 : 0,
          zIndex: accountCount - index,
        }}
      >
        {account.id ? (
          getInitials(account?.profile?.fullName ?? account.email ?? "")
        ) : account.authStrategy === "PHONE_AUTH" ? (
          <PhoneIcon width={23.25} height={23.2} />
        ) : (
          <EnvelopeIcon width={23.25} height={23.2} />
        )}
      </Box>
    );
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ cursor: "pointer" }}
    >
      {accountCount >= 1 && renderAccount(accounts[0], 0)}
      {accountCount >= 2 && renderAccount(accounts[1], 1)}
      {accountCount > 2 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: theme.palette.others.disabled,
            justifyContent: "center",
            width: 32,
            height: 32,
            fontSize: "sm",
            color: theme.palette.common.white,
            bgcolor: theme.palette.primary[500],
            borderRadius: "50%",
            ml: -1.5,
            zIndex: 1,
          }}
        >
          +{accountCount - 2}
        </Box>
      )}
    </Stack>
  );
};

const CatalogueCard = ({
  catalog,
  handleDelete,
  handleDuplicate,
}: {
  catalog: CatalogType;
  handleDelete: () => void;
  handleDuplicate: () => void;
}) => {
  const { t } = useTranslation();
  console.log("CATALOG", catalog);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // memoize catalog's info, only recalculate if catalog changes
  const CATALOG_METADATA = useMemo(() => {
    return [
      // Currency
      catalog?.currency && {
        title: t("catalog.catalogCurrency"),
        value: catalog.currency,
      },

      // Tax ID
      catalog?.taxId && {
        title: t("catalog.catalogTax"),
        value: catalog.taxId.taxLabel as string,
      },

      // Catalog Period
      {
        title: t("catalog.catalogPeriod"),
        value: (
          <Typography
            level="text-xs-semibold"
            textColor={
              dayjs().isAfter(catalog?.periodEndDate)
                ? theme.palette.danger[400]
                : theme.palette.common.black
            }
          >
            :{" "}
            {catalog?.periodStartDate
              ? `${dayjs(catalog.periodStartDate).format(
                  "YYYY-MM-DD"
                )} ~ ${dayjs(catalog.periodEndDate).format("YYYY-MM-DD")}`
              : "-"}
          </Typography>
        ),
      },

      // Last Updated
      {
        title: t("catalog.catalogLastUpdated"),
        value: dayjs(catalog?.updatedAt).format("YYYY-MM-DD"),
      },

      // Total Items
      catalog?.itemsCount && {
        title: t("catalog.catalogTotalItems"),
        value: `${catalog.itemsCount} Items`,
      },
    ].filter(Boolean) as { title: string; value: string | ReactNode }[];
  }, [catalog]);

  return (
    <ListItem
      sx={{
        py: { xs: "1.5rem", md: "0.5rem", lg: "1rem" },
        px: { xs: "1.5rem", md: "10px" },
        my: "16px",
        display: { xs: "block", md: "flex" },
        alignItems: "center",
        width: "100%",
        borderRadius: { xs: "8px", md: 0 },
        border: "1px solid",
        borderTop: { md: "none" },
        borderLeft: { md: "none" },
        borderRight: { md: "none" },
        borderBottom: { md: "1px solid" },
        borderColor: theme.colorSchemes.light.palette.others.disabled,
      }}
    >
      <Box
        sx={{
          mb: { xs: "0", md: "0.5rem" },
        }}
      >
        {/* catalog title */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{
            justifyContent: {
              xs: "space-between",
              md: "flex-start",
            },
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: "4px", md: "12px" },
            mb: "16px",
          }}
        >
          {/* chips & menu button */}
          <Stack
            direction={"row"}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* chips */}
            <Stack direction={"row"} gap={"4px"} maxHeight={"24px"}>
              {/* expired chip */}
              {dayjs().isAfter(dayjs(catalog.periodEndDate)) ? (
                <Chip
                  color="neutral"
                  sx={{
                    borderRadius: "4px",
                    paddingY: "4px",
                    paddingX: "8px",
                    backgroundColor: theme.palette.neutral[400],
                  }}
                >
                  <Typography
                    level="text-xs-semibold"
                    textColor={theme.palette.common.white}
                  >
                    Expired
                  </Typography>
                </Chip>
              ) : (
                <Chip
                  color="success"
                  sx={{
                    borderRadius: "4px",
                    paddingY: "4px",
                    paddingX: "8px",
                    backgroundColor: theme.palette.others.active_chip_bg,
                  }}
                >
                  <Typography level="text-xs-semibold">Active</Typography>
                </Chip>
              )}

              {/* private catalog chip */}
              <If condition={catalog.catalogType === "PRIVATE"}>
                <Chip
                  color="success"
                  sx={{
                    borderRadius: "4px",
                    paddingY: "4px",
                    paddingX: "8px",
                    backgroundColor: theme.palette.others.active_chip_bg,
                  }}
                >
                  <LockClosedIcon width={16} height={16} />
                </Chip>
              </If>

              {/* private catalog title */}
              <Typography
                level="text-lg-semibold"
                textColor={"neutral.900"}
                display={{ xs: "none", md: "block" }}
              >
                {catalog.catalogName}
              </Typography>
            </Stack>

            {/* delete btn, duplicate btn xs */}
            <Box display={{ xs: "block", md: "none" }}>
              <Dropdown
                id={`catalog-${catalog.id}-mobile-menu`}
                open={mobileMenuOpen}
                onOpenChange={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                }}
              >
                <MenuButton
                  slots={{ root: Button }}
                  slotProps={{
                    root: {
                      variant: "linkz-outlined-neutral",
                      color: "neutral",
                      sx: {
                        "&:hover": {
                          bgcolor: "transparent",
                        },
                        "&:focus": {
                          bgcolor: "transparent",
                        },
                        padding: 0,
                        minWidth: "40px",
                        maxHeight: "40px",
                      },
                    },
                  }}
                >
                  <EllipsisVerticalIcon width={25} height={25} />
                </MenuButton>
                <Menu>
                  {/* duplicate btn */}
                  <MenuItem
                    sx={{
                      gap: "1rem",
                    }}
                    onClick={handleDuplicate}
                  >
                    <CopyAll sx={{ width: "18px", height: "18px" }} />
                    {t("catalog.duplicateBtn")}
                  </MenuItem>

                  {/* delete btn */}
                  <MenuItem
                    sx={{
                      gap: "1rem",
                    }}
                    onClick={handleDelete}
                  >
                    <DeleteOutline sx={{ width: "18px", height: "18px" }} />
                    {t("catalog.deleteCatalogBtn")}
                  </MenuItem>
                </Menu>
              </Dropdown>
            </Box>
          </Stack>

          {/* catalog name xs */}
          <Typography
            level={"text-md-semibold"}
            sx={{
              color: theme.palette.common.black,
              display: { xs: "block", md: "none" },
            }}
          >
            {catalog.catalogName}
          </Typography>

          {/* catalog name lg */}
          <Typography
            level={"text-lg-semibold"}
            sx={{
              color: theme.palette.common.black,
              display: { xs: "none", md: "black" },
            }}
          >
            {catalog.catalogName}
          </Typography>
        </Stack>

        {/* catalog details */}
        <Stack
          direction={"column"}
          alignItems={"flex-start"}
          gap={{ lg: "44px" }}
        >
          <CatalogueMetadata
            data={CATALOG_METADATA}
            containerStyle={{}}
            columns={2}
          />
        </Stack>
      </Box>

      {/* buttons */}
      <Stack
        direction={"row"}
        sx={{
          alignItems: "center",
          gap: { md: "12px" },
          justifyContent: { xs: "space-between", md: "flex-end" },
          ml: { md: "auto" },
          mt: { xs: "16px", md: 0 },
        }}
      >
        {/* invited users */}
        <CatalogInvitationData catalogId={catalog.id} />

        {/* view catalog */}
        <Link
          to={`/client/catalogues/${catalog.id}/items`}
          className="lg:mx-auto lg:ml-auto lg:mr-0"
        >
          <Button
            variant="linkz-filled-primary"
            sx={{
              width: { xs: "213px", md: "87px" },
            }}
          >
            {t("catalog.viewBtn")}
          </Button>
        </Link>

        {/* duplicate button */}
        <Button
          variant="linkz-outlined-primary"
          onClick={handleDuplicate}
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          {t("catalog.duplicateBtn")}
        </Button>

        {/* delete button */}
        <Button
          variant="plain"
          size="sm"
          onClick={handleDelete}
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          <DeleteOutline color="error" sx={{ height: "32px", width: "32px" }} />
        </Button>
      </Stack>
    </ListItem>
  );
};

export default ListCatalogue;
